import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { toast } from "sonner";
import { ArrowLeft, FileStack, FileDown, History, ClipboardList, ArrowRight, CalendarClock } from "lucide-react";
import AiDraftsPanel from "./AiDraftsPanel";

const STATE_TONE = {
  identified: "text-muted-foreground border-border",
  eligibility_assessment: "text-[#00B4E6] border-[#00B4E6]/40",
  qualified: "text-[#00B4E6] border-[#00B4E6]/40",
  approved_for_pursuit: "text-accent border-accent/50",
  documents_in_preparation: "text-[#FFB800] border-[#FFB800]/40",
  application_ready: "text-accent border-accent/50",
  submitted: "text-[#FFB800] border-[#FFB800]/40",
  clarification_requested: "text-[#FFB800] border-[#FFB800]/40",
  approved: "text-[#00E65B] border-[#00E65B]/40",
  rejected: "text-red-400 border-red-400/40",
  contracted: "text-[#00E65B] border-[#00E65B]/40",
  implementation: "text-[#00E65B] border-[#00E65B]/40",
  claim_preparation: "text-[#FFB800] border-[#FFB800]/40",
  claim_submitted: "text-[#FFB800] border-[#FFB800]/40",
  payment_received: "text-[#00E65B] border-[#00E65B]/40",
  closed: "text-muted-foreground border-border",
};
const ITEM_TONE = { validated: "text-[#00E65B]", uploaded: "text-accent", requested: "text-[#00B4E6]", rejected: "text-red-400", expired: "text-red-400", not_started: "text-muted-foreground" };

export async function downloadDossier(iid, name) {
  try {
    const r = await api.get(`/susfin/dossier/${iid}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `funding_readiness_dossier_${(name || iid).slice(0, 30).replace(/\W+/g, "_")}.pdf`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Funding Readiness Dossier downloaded");
  } catch (e) { toast.error(apiErr(e)); }
}

function StateStepper({ states, current }) {
  const idx = states.findIndex((s) => s.value === current);
  return (
    <div className="flex flex-wrap gap-1" data-testid="sf-app-stepper">
      {states.map((s, n) => (
        <span key={s.value}
          className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm border ${n === idx ? `${STATE_TONE[s.value]} font-bold` : n < idx ? "text-muted-foreground border-border bg-surface-hover" : "text-muted-foreground/50 border-border/50"}`}>
          {s.label}
        </span>
      ))}
    </div>
  );
}

function MilestoneCard({ a, onUpdated }) {
  const ms = a.next_milestone;
  const [label, setLabel] = useState(ms?.label || "");
  const [due, setDue] = useState(ms?.due_date || "");
  const [busy, setBusy] = useState(false);
  const save = async (clear = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/susfin/applications/${a.id}/milestone`,
        clear ? {} : { label: label || null, due_date: due || null });
      onUpdated(r.data);
      if (clear) { setLabel(""); setDue(""); }
      toast.success(clear ? "Milestone cleared" : "Milestone saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-flat p-4" data-testid="sf-app-milestone">
      <div className="label-eyebrow text-[10px] mb-2 inline-flex items-center gap-1">
        <CalendarClock className="h-3.5 w-3.5 text-accent" /> Next milestone / deadline
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <Input className="h-8 text-xs" placeholder="e.g. Claim submission deadline" value={label}
            onChange={(e) => setLabel(e.target.value)} data-testid="sf-app-milestone-label" />
        </div>
        <Input type="date" className="h-8 text-xs w-40" value={due} onChange={(e) => setDue(e.target.value)} data-testid="sf-app-milestone-date" />
        <Button size="sm" variant="primary" className="h-8" disabled={busy || (!label && !due)} onClick={() => save(false)} data-testid="sf-app-milestone-save">Save</Button>
        {ms && <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => save(true)} data-testid="sf-app-milestone-clear">Clear</Button>}
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5">Shown on the Funding Calendar with overdue / due-soon alerts.</p>
    </div>
  );
}

function AppDetail({ aid, onBack, reference }) {
  const [a, setA] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/susfin/applications/${aid}`).then((r) => setA(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [aid]);
  if (!a) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading application…</div>;

  const transition = async (state) => {
    setBusy(true);
    try {
      const r = await api.post(`/susfin/applications/${aid}/transition`, { state, note: note || null });
      setA(r.data); setNote("");
      toast.success(`Moved to ${r.data.state_label}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const setItem = async (key, status) => {
    try {
      const r = await api.post(`/susfin/applications/${aid}/checklist`, { key, status });
      setA(r.data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const groups = [];
  (a.checklist || []).forEach((x) => {
    let g = groups.find((y) => y.key === x.group);
    if (!g) { g = { key: x.group, label: x.group_label, items: [] }; groups.push(g); }
    g.items.push(x);
  });

  return (
    <div className="space-y-5" data-testid="sf-app-detail">
      <div>
        <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1" data-testid="sf-app-back">
          <ArrowLeft className="h-4 w-4" /> Applications
        </button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2 flex-wrap">
              {a.opportunity_name}
              <Badge variant="outline" className={`text-[9px] uppercase ${STATE_TONE[a.state]}`} data-testid="sf-app-state">{a.state_label}</Badge>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{a.provider} · {a.initiative_name} · checklist {a.checklist_pct}% validated</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => downloadDossier(a.initiative_id, a.initiative_name)} data-testid="sf-app-dossier-btn">
            <FileDown className="h-4 w-4" /> Dossier PDF
          </Button>
        </div>
      </div>

      <div className="card-flat p-4">
        <StateStepper states={reference.states} current={a.state} />
        {a.allowed_transitions.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-4">
            <Input className="h-8 text-xs max-w-xs" placeholder="Transition note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="sf-app-note" />
            {a.allowed_transitions.map((t) => (
              <Button key={t.value} size="sm" variant={["rejected", "closed"].includes(t.value) ? "outline" : "primary"} disabled={busy}
                onClick={() => transition(t.value)} data-testid={`sf-app-move-${t.value}`}>
                {t.label} <ArrowRight className="h-3 w-3" />
              </Button>
            ))}
          </div>
        )}
      </div>

      <MilestoneCard key={a.next_milestone?.set_at || "ms"} a={a} onUpdated={setA} />

      {a.initiative_id && (
        <div data-testid="sf-app-ai-draft">
          <AiDraftsPanel iid={a.initiative_id} only="grant_justification" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 card-flat p-4" data-testid="sf-app-checklist">
          <div className="label-eyebrow mb-1 inline-flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5 text-accent" /> Document checklist</div>
          <Progress value={a.checklist_pct} className="h-1.5 mb-3" />
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.key}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">{g.label}</div>
                <div className="space-y-1">
                  {g.items.map((x) => (
                    <div key={x.key} className="flex items-center justify-between gap-2 border border-border/60 rounded-sm px-2.5 py-1.5" data-testid={`sf-app-item-${x.key}`}>
                      <span className="text-xs">{x.label}</span>
                      <Select value={x.status} onValueChange={(v) => setItem(x.key, v)}>
                        <SelectTrigger className={`h-7 w-36 text-[11px] ${ITEM_TONE[x.status]}`} data-testid={`sf-app-item-status-${x.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {reference.item_statuses.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-flat p-4 h-fit" data-testid="sf-app-audit">
          <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><History className="h-3.5 w-3.5 text-accent" /> Audit history</div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {[...(a.audit || [])].reverse().map((e, idx) => (
              <div key={idx} className="text-[11px] border-l-2 border-border pl-2">
                <div className="text-muted-foreground font-mono text-[9px]">{e.at?.slice(0, 16).replace("T", " ")} · {e.by}</div>
                <div>{e.detail || e.action}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ApplicationsTab({ openApplicationId, clearOpenApplication }) {
  const [items, setItems] = useState(null);
  const [reference, setReference] = useState(null);
  const [active, setActive] = useState(null);

  const load = async () => {
    const [r, ref] = await Promise.all([api.get("/susfin/applications"), api.get("/susfin/app-reference")]);
    setItems(r.data.items); setReference(ref.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (openApplicationId) { setActive(openApplicationId); clearOpenApplication?.(); }
  }, [openApplicationId]);

  if (!items || !reference) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading applications…</div>;
  if (active) return <AppDetail aid={active} onBack={() => { setActive(null); load(); }} reference={reference} />;

  return (
    <div className="space-y-3" data-testid="sf-applications">
      {items.length === 0 && (
        <div className="card-flat p-12 text-center text-muted-foreground" data-testid="sf-apps-empty">
          <FileStack className="h-8 w-8 mx-auto mb-3 opacity-50" />
          No funding applications yet — run funding discovery on a register investment and start an application from a match card.
        </div>
      )}
      {items.map((a) => (
        <button key={a.id} onClick={() => setActive(a.id)} data-testid={`sf-app-row-${a.id}`}
          className="w-full text-left card-flat p-4 hover:bg-surface-hover transition-colors flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-semibold truncate">{a.opportunity_name}</span>
              <Badge variant="outline" className={`text-[9px] uppercase ${STATE_TONE[a.state]}`}>{a.state_label}</Badge>
              <Badge variant="outline" className="text-[9px] uppercase">{a.source === "scheme" ? "Scheme" : "Bank offer"}</Badge>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 truncate">
              {a.provider} · {a.initiative_name}
              {a.next_milestone?.due_date && (
                <span className="ml-2 inline-flex items-center gap-1 text-[#FFB800]" data-testid={`sf-app-row-milestone-${a.id}`}>
                  <CalendarClock className="h-3 w-3" /> {a.next_milestone.label} · {a.next_milestone.due_date}
                </span>
              )}
            </div>
          </div>
          <div className="w-40 shrink-0">
            <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
              <span>Checklist</span><span className="font-mono">{a.checklist_pct}%</span>
            </div>
            <Progress value={a.checklist_pct} className="h-1" />
          </div>
        </button>
      ))}
    </div>
  );
}
